import React, { useState, useEffect, useMemo } from 'react';

// Comps
import { CustomFields, InputField } from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@arctheme-components/blocks/ButtonSpinner/ButtonSpinner';
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';

// Hooks
import { useGetCustomerQuery } from '@src/hooks/useGetCustomerQuery';
import useCustomerUpdate from '@src/hooks/useCustomerUpdate';
import {Button} from "@src/stories/elements/Button/Button";

const AccountProfileForm = ({ section, signUpFields }) => {
  const {
    first_name_label,
    first_name_placeholder,
    last_name_label,
    last_name_placeholder,
    email_label,
    email_placeholder,
    button_label,
    button_style,
    success_message
  } = section;

  // Sort custom fields
  const customSignUpFields = signUpFields?.sort((a, b) => {
    const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
    const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
    return orderA - orderB;
  });

  // Hooks
  const { fetchCustomer, customer } = useGetCustomerQuery();
  const {
    updateCustomer,
    loading: updateCustomerLoading,
    success: updateCustomerSuccess,
    error: updateCustomerError,
    fieldsWithError: updateCustomerFieldsWithError
  } = useCustomerUpdate();

  // States
  const initialDependenciesState = useMemo(() => {
    return Object.assign(
      {},
      {
        firstName: '',
        lastName: '',
        email: ''
      },
      ...customSignUpFields.map((field) => ({
        [field.metafield_key]: ''
      }))
    );
  }, [customer]);
  const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
  const [isUpdated, setIsUpdated] = useState(false);

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === 'checkbox' ? checked : value;

    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    const newData = {
      ...dependenciesState,
      [name]: value
    };

    setDependenciesState(newData);
    toggleIsUpdated(newData);
  };

  const toggleIsUpdated = (newData) => {
    const updated = Object.keys(newData).some((key) => customer[key] !== newData[key]);

    setIsUpdated(updated);
  };

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    updateCustomer(dependenciesState, (window as any).customer.id, customSignUpFields);
  };

  // Fetch customer
  useEffect(() => {
    fetchCustomer((window as any).customer.id, customSignUpFields);
  }, []);

  // And then pre-fill the fields
  useEffect(() => {
    if (customer) {
      for (const key in customer) {
        console.log(key);
        if (key in dependenciesState) {
          setDependenciesState((prev) => ({
            ...prev,
            [key]: customer[key]
          }));
        }
      }
    }
  }, [customer]);

  const showCustomFields = true;

  console.log(dependenciesState);
  return (
      <AccountSectionLayout classes='lg:pl-[98px] md:pt-8 hide-scrollbar'>

        <div className={'flex flex-col lg:flex-row flex-wrap gap-[41px] lg:gap-[98px]'}>
          <div className={'flex flex-col w-full lg:w-[315px]'}>
            <h4>PERSONAL INFORMATION</h4>
            <form
                id='editForm'
                onSubmit={handleSubmit}
                className='account-page__form pt-[16px]'
            >
              <div className='flex flex-col gap-y-4 pb-[40px]'>
                {/* First Name */}
                <InputField
                    inputType='text'
                    id='firstNameRegister'
                    name='firstName'
                    value={dependenciesState['firstName']}
                    onChange={handleInputChange}
                    required={true}
                    disabled={true}
                    label={first_name_label}
                    placeholder={first_name_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('firstName')}
                />

                {/* Last Name */}
                <InputField
                    inputType='text'
                    id='lastNameRegister'
                    name='lastName'
                    value={dependenciesState['lastName']}
                    onChange={handleInputChange}
                    required={true}
                    disabled={true}
                    label={last_name_label}
                    placeholder={last_name_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('lastName')}
                />

                {/* Email */}
                <InputField
                    inputType='email'
                    id='emailRegister'
                    name='email'
                    value={dependenciesState['email']}
                    onChange={handleInputChange}
                    required={true}
                    disabled={true}
                    label={email_label}
                    placeholder={email_placeholder}
                    hasError={updateCustomerFieldsWithError?.includes('email')}
                />

                {/* Custom Fields */}
                {showCustomFields && (
                    <CustomFields
                        customSignUpFields={customSignUpFields}
                        onChange={handleInputChange}
                        values={dependenciesState}
                        fieldsWithError={updateCustomerFieldsWithError}
                    />
                )}

                {updateCustomerSuccess && (
                    <Paragraph
                        type='text-center success'
                        text={success_message}
                    />
                )}

                {updateCustomerError && (
                    <Paragraph
                        type='errors text-center'
                        text={updateCustomerError}
                    />
                )}

                <ButtonSpinner
                    classes={`hidden w-full ${button_style} h-[calc(3rem-2px)] text-base`}
                    isLoading={updateCustomerLoading}
                    ariaLabel='Submit edit profile form'
                    disabled={!isUpdated}
                >
                  {button_label}
                </ButtonSpinner>
              </div>
            </form>

            <div className={'flex flex-col gap-y-[46px]'}>
              <div className={`flex flex-col`}>
                <h4 className={'mb-4 lg:mb-1'}>ORDER HISTORY</h4>
                <p className={'b2'}>View your recent order history.</p>
                <Button
                    className={'button button--primary w-full mt-[16px]'}
                    link={'/account?view=orders'}
                    label={'View Order History'}
                />
              </div>
              <div className={`flex flex-col`}>
                <h4 className={'mb-4 lg:mb-1'}>WISHLIST</h4>
                <p className={'b2'}>Create a list of your favourite items</p>
                <Button
                    className={'button button--primary w-full mt-[16px]'}
                    link={'/account?view=wishlist'}
                    label={'View Wishlist'}
                />
              </div>
              <div className={`flex flex-col`}>
                <h4 className={'mb-4 lg:mb-1'}>ADDRESS BOOK</h4>
                <p className={'b2'}>Add all of your prefered shipping addresses.</p>
                <Button
                    className={'button button--primary w-full mt-[16px]'}
                    link={'/account?view=addresses'}
                    label={'View Address Book'}
                />
              </div>
            </div>
          </div>

          <div className={`flex flex-col w-full lg:max-w-[307px]`}>
            <h4>CONTACT US</h4>
            <p className={`b2 pt-4 lg:pt-1`}>Contact customer care for further assistance.</p>
            <p className="b2 pt-[17px]">
              P - <a href="tel:+61418114745" className="cursor-pointer">(+61) 418 114 745</a>
            </p>
            <p className="b2 pt-2">
              E - <a href="mailto:online@leemathews.com.au" className="cursor-pointer">online@leemathews.com.au</a>
            </p>
          </div>
        </div>


      </AccountSectionLayout>
  );
};

export default AccountProfileForm;
